import { Component, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { FloatLabel } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CompanyService } from '../../../../services/remote/company.service';
import { NotificationService } from '../../../../services/shared/notification.service';
import { Company } from '../../../../types/company';
import { License } from '../../../../types/license';
import { CompanyFormDialogComponent } from '../company-form-dialog/company-form-dialog.component';
import { LicenseFormDialogComponent } from '../license-form-dialog/license-form-dialog.component';
import { LicenseListComponent } from '../license-list/license-list.component';
import { QueryResult } from '../license/types/licenseTypes';

@Component({
  selector: 'cognos-company-list',
  imports: [
    AccordionModule,
    ButtonModule,
    CardModule,
    LicenseListComponent,
    CompanyFormDialogComponent,
    LicenseFormDialogComponent,
    FloatLabel,
    InputTextModule,
    SelectModule,
    FormsModule,
    ConfirmDialog,
  ],
  providers: [ConfirmationService],
  templateUrl: './company-list.component.html',
  styleUrl: './company-list.component.scss',
})
export class CompanyListComponent {
  public companies$ = this._companyService.companies$;

  public isAddCompanyDialogVisible = false;

  public companyToEdit: Company | undefined = undefined;

  public selectedCompanyId: string | undefined;
  public selectedLicense: any;
  public isAddLicenseDialogVisible = false;

  public companyFilter = signal<string | null>(null);

  public sortedCompanyNames = computed(() =>
    this.companies$()
      .map((company: Company) => company.name)
      .sort((a: string, b: string) => a.localeCompare(b))
  );

  public filteredCompanies = computed(() => {
    const companyName = this.companyFilter();
    if (!companyName) return this.companies$();
    return this.companies$().filter(
      (company: Company) => company.name === companyName
    );
  });

  constructor(
    private _companyService: CompanyService,
    private _notificationService: NotificationService,
    private _confirmationService: ConfirmationService
  ) {}

  public handleAddCompanyClick() {
    this.companyToEdit = undefined;
    this.isAddCompanyDialogVisible = true;
  }

  public handleEditCompanyClick(event: Event, company: Company): void {
    event.stopPropagation();
    this.companyToEdit = company;
    this.isAddCompanyDialogVisible = true;
  }

  public handleDeleteCompanyClick(event: Event, companyId: string): void {
    event.stopPropagation();
    this._confirmDeletion(event, companyId);
  }

  private _confirmDeletion(event: Event, companyId: string) {
    this._confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Do you want to delete this company?',
      header: 'Delete Confirmation',
      icon: 'pi pi-trash',
      rejectLabel: 'Cancel',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        raised: true,
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger',
        raised: true,
      },
      accept: async () => {
        const result = await this._companyService.deleteCompany(companyId);
        if (result === QueryResult.ok) {
          this._companyService.fetchCompanies();
          this._notificationService.showSuccessNotification(
            'Company was succesfully deleted.'
          );
        } else {
          this._notificationService.showErrorNotification(
            'Deletion failed. Please try again.'
          );
        }
      },
      reject: () => {},
    });
  }

  public handleAddLicenseClick(companyId: string): void {
    this.selectedCompanyId = companyId;
    this.selectedLicense = undefined;
    this.isAddLicenseDialogVisible = true;
  }

  handleEditLicense(licenseToEdit: License, companyId: string) {
    this.selectedCompanyId = companyId;
    this.selectedLicense = licenseToEdit;
    this.isAddLicenseDialogVisible = true;
  }
}
